/**
 * WordPress dependencies
 */
import { ToolsPanel, ToolsPanelItem } from '@safe-wordpress/components';
import { _x } from '@safe-wordpress/i18n';

/**
 * Internal dependencies
 */
import type { ToolsPanelProps } from './types';
import {
	getPreviewValue,
	hasInheritedValue,
	hasUserValue,
} from '../../../../utils/props';
import { AnglePickerControl, UnitWithRangeControl } from './internal';

export function FiltersPanel( {
	value: props,
	onChange,
}: ToolsPanelProps ): JSX.Element {
	// Filters
	const filterValue = getPreviewValue( props.filter );
	const parsedFilterValues = parseFilter( filterValue );

	const onChangeFilterFunction =
		( filterFunction: FilterFunction ) =>
		( newValue?: number | string ) => {
			const updatedParsedValues = {
				...parsedFilterValues,
				[ filterFunction ]: newValue,
			};
			const updatedFilterValue = composeFilter( updatedParsedValues );
			onChange( { filter: updatedFilterValue } );
		};

	const resetAll = () =>
		onChange( {
			filter: undefined,
		} );

	return (
		<ToolsPanel
			label={ _x( 'Filters', 'text', 'nelio-ab-testing' ) }
			resetAll={ resetAll }
		>
			<ToolsPanelItem
				label={ _x( 'Filters', 'text', 'nelio-ab-testing' ) }
				hasValue={ () => hasUserValue( props.filter ) }
				onSelect={ () => onChange( { filter: 'none' } ) }
				onDeselect={ () =>
					onChange( {
						filter: undefined,
					} )
				}
				isShownByDefault={ hasInheritedValue( props.filter ) }
			>
				<div className="nab-components-filter-control">
					<UnitWithRangeControl
						label={ _x( 'Blur', 'text', 'nelio-ab-testing' ) }
						units={ [ 'px', 'em', 'rem' ] }
						value={ parsedFilterValues.blur ?? '0px' }
						onChange={ onChangeFilterFunction( 'blur' ) }
						initialPosition={ 0 }
						disabled={ props.filter?.type === 'readonly' }
						__next40pxDefaultSize
					/>
					<UnitWithRangeControl
						label={ _x( 'Brightness', 'text', 'nelio-ab-testing' ) }
						units={ [ '%' ] }
						value={ parsedFilterValues.brightness ?? 1 }
						onChange={ onChangeFilterFunction( 'brightness' ) }
						min={ 0 }
						initialPosition={ 1 }
						disabled={ props.filter?.type === 'readonly' }
						__next40pxDefaultSize
					/>
					<UnitWithRangeControl
						label={ _x( 'Contrast', 'text', 'nelio-ab-testing' ) }
						units={ [ '%' ] }
						value={ parsedFilterValues.contrast ?? '100%' }
						onChange={ onChangeFilterFunction( 'contrast' ) }
						min={ 0 }
						initialPosition={ 100 }
						disabled={ props.filter?.type === 'readonly' }
						__next40pxDefaultSize
					/>
					<UnitWithRangeControl
						label={ _x( 'Grayscale', 'text', 'nelio-ab-testing' ) }
						units={ [ '%' ] }
						value={ parsedFilterValues.grayscale ?? '0%' }
						onChange={ onChangeFilterFunction( 'grayscale' ) }
						min={ 0 }
						initialPosition={ 0 }
						disabled={ props.filter?.type === 'readonly' }
						__next40pxDefaultSize
					/>
					<AnglePickerControl
						label={ _x( 'Hue Rotate', 'text', 'nelio-ab-testing' ) }
						value={ parsedFilterValues[ 'hue-rotate' ] ?? '0' }
						onChange={ onChangeFilterFunction( 'hue-rotate' ) }
						disabled={ props.filter?.type === 'readonly' }
					/>
					<UnitWithRangeControl
						label={ _x( 'Invert', 'text', 'nelio-ab-testing' ) }
						units={ [ '%' ] }
						value={ parsedFilterValues.invert ?? '0%' }
						onChange={ onChangeFilterFunction( 'invert' ) }
						min={ 0 }
						initialPosition={ 0 }
						disabled={ props.filter?.type === 'readonly' }
						__next40pxDefaultSize
					/>
					<UnitWithRangeControl
						label={ _x( 'Opacity', 'text', 'nelio-ab-testing' ) }
						units={ [ '%' ] }
						value={ parsedFilterValues.opacity ?? '100%' }
						onChange={ onChangeFilterFunction( 'opacity' ) }
						min={ 0 }
						initialPosition={ 100 }
						disabled={ props.filter?.type === 'readonly' }
						__next40pxDefaultSize
					/>
					<UnitWithRangeControl
						label={ _x( 'Saturate', 'text', 'nelio-ab-testing' ) }
						units={ [ '%' ] }
						value={ parsedFilterValues.saturate ?? '100%' }
						onChange={ onChangeFilterFunction( 'saturate' ) }
						min={ 0 }
						initialPosition={ 100 }
						disabled={ props.filter?.type === 'readonly' }
						__next40pxDefaultSize
					/>
					<UnitWithRangeControl
						label={ _x( 'Sepia', 'text', 'nelio-ab-testing' ) }
						units={ [ '%' ] }
						value={ parsedFilterValues.sepia ?? '0%' }
						onChange={ onChangeFilterFunction( 'sepia' ) }
						min={ 0 }
						initialPosition={ 0 }
						disabled={ props.filter?.type === 'readonly' }
						__next40pxDefaultSize
					/>
				</div>
			</ToolsPanelItem>
		</ToolsPanel>
	);
}

// =======
// HELPERS
// =======

type FilterFunction =
	| 'blur'
	| 'brightness'
	| 'contrast'
	| 'grayscale'
	| 'invert'
	| 'opacity'
	| 'hue-rotate'
	| 'saturate'
	| 'sepia'
	| 'drop-shadow';

function parseFilter( filterValue: string ) {
	const regex = /(\b[a-zA-Z\-]+)\(([^)]+)\)/g;
	let match;
	const filters: Partial< Record< FilterFunction, string > > = {};

	while ( ( match = regex.exec( filterValue ) ) !== null ) {
		if ( match[ 1 ] && match[ 2 ] ) {
			filters[ match[ 1 ] as FilterFunction ] = match[ 2 ]
				.trim()
				.replace( 'deg', '' );
		}
	}

	return filters;
}

function composeFilter(
	filters: Partial< Record< FilterFunction, string > >
): string {
	return Object.entries( filters )
		.map(
			( [ key, value ] ) =>
				`${ key }(${ key === 'hue-rotate' ? value + 'deg' : value })`
		)
		.join( ' ' );
}
